import { Link } from 'react-router-dom';

type Lp = (path: string) => string;

export function ContactBodyEn({ lp }: { lp: Lp }) {
  return (
    <article className="about-section">
      <h1>Contact Us</h1>
      <p>
        We appreciate your interest in <strong>Lotto Number Generator</strong>. Whether you have a question about how the tool works,
        want to report a bug, or have a business inquiry, we are here to help. Please read through the sections below to find the
        best way to reach us. We strive to respond to all inquiries in a timely manner and value every piece of feedback we receive
        from our users.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>General Inquiries</h2>
      <p>
        For general questions about the Lotto Number Generator, including how the random number generation works, how the historical
        jackpot matching feature operates, or questions about the differences between Powerball, Mega Millions, and Korean Lotto 6/45
        number ranges, please email us at:
      </p>
      <p style={{ textAlign: 'center', fontSize: '1.1em', margin: '16px 0' }}>
        <strong>
          <a href="mailto:contact@lotto-number-generator.com">contact@lotto-number-generator.com</a>
        </strong>
      </p>

      <p>
        Before reaching out, we recommend checking our <Link to={lp('/faq')}>FAQ page</Link>, which answers the most common questions about
        lottery odds, how pseudo-random number generation works, what &ldquo;hot&rdquo; and &ldquo;cold&rdquo; numbers mean, and how
        our historical jackpot matching database is maintained. You may also find helpful information on our <Link to={lp('/about')}>About</Link> page
        and our <Link to={lp('/story')}>Story</Link> page, which covers the founding vision behind this tool.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>Bug Reports and Feedback</h2>
      <p>
        If you encounter a technical issue while using the generator — such as numbers not appearing, the copy or save feature not
        working, display problems on your device, or any other unexpected behavior — please let us know. When reporting a bug, it
        helps if you include the following details so we can investigate and resolve the issue quickly:
      </p>
      <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.8, color: 'var(--text-secondary)' }}>
        <li>The device and browser you were using (for example, iPhone Safari, Chrome on Windows, etc.)</li>
        <li>Which lottery game was selected when the issue occurred (Powerball, Mega Millions, or Korean Lotto 6/45)</li>
        <li>A brief description of what happened versus what you expected</li>
        <li>A screenshot if possible — this makes it much easier for us to reproduce the problem</li>
      </ul>
      <p>
        We also welcome suggestions for new features, improvements to the user interface, or ideas for additional lottery games you would
        like to see supported. Your feedback directly shapes our development roadmap and helps us serve you better.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>Response Times</h2>
      <p>
        We are a small team, so please allow up to <strong>3–5 business days</strong> for a response to general inquiries and bug reports.
        We prioritize issues that affect core functionality — such as number generation or data accuracy — and address them as quickly as
        possible. Feature requests and general questions are reviewed on a rolling basis. If your matter is urgent, please note that in
        the subject line of your email and we will do our best to expedite our response.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>Business and Advertising Inquiries</h2>
      <p>
        For partnership opportunities, advertising placements, media coverage, or other business-related discussions, please include
        &ldquo;Business Inquiry&rdquo; in the subject line of your email to{' '}
        <a href="mailto:contact@lotto-number-generator.com">contact@lotto-number-generator.com</a>. We are open to collaborations that
        align with our mission of providing free, entertaining, and educational lottery tools to users around the world.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>Privacy and Data Requests</h2>
      <p>
        If you have questions about how your data is handled, wish to exercise your privacy rights, or need to request data deletion,
        please refer to our <Link to={lp('/privacy')}>Privacy Policy</Link> for full details. You may also email us directly with the subject
        line &ldquo;Privacy Request&rdquo; and we will process your request in accordance with applicable regulations. We take data
        protection seriously and respond to all privacy-related inquiries within the timeframes required by law.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>Frequently Asked Questions About Contacting Us</h2>
      <div style={{ background: 'var(--info-bg)', borderRadius: 10, padding: '14px 18px', marginBottom: 10 }}>
        <h3 style={{ fontSize: '0.95em', fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
          Do you provide phone support?
        </h3>
        <p style={{ fontSize: '0.9em', lineHeight: 1.55, margin: 0, color: 'var(--text-secondary)' }}>
          At this time, we only provide support via email. This allows us to give each inquiry the attention it deserves and maintain
          a written record of all communications for quality assurance purposes.
        </p>
      </div>
      <div style={{ background: 'var(--info-bg)', borderRadius: 10, padding: '14px 18px', marginBottom: 10 }}>
        <h3 style={{ fontSize: '0.95em', fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
          Can I request a new lottery game be added?
        </h3>
        <p style={{ fontSize: '0.9em', lineHeight: 1.55, margin: 0, color: 'var(--text-secondary)' }}>
          We currently support US Powerball, Mega Millions, and Korean Lotto 6/45. If there is a specific national or state lottery you
          would like us to add, let us know the game name, number ranges, and draw schedule and we will evaluate it for a future update.
        </p>
      </div>
      <div style={{ background: 'var(--info-bg)', borderRadius: 10, padding: '14px 18px', marginBottom: 10 }}>
        <h3 style={{ fontSize: '0.95em', fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
          I found incorrect historical data. How do I report it?
        </h3>
        <p style={{ fontSize: '0.9em', lineHeight: 1.55, margin: 0, color: 'var(--text-secondary)' }}>
          Our historical jackpot database is updated weekly from official public data sources including data.ny.gov for US draws and
          the Korean National Lottery for Lotto 6/45. If you believe a draw result is incorrect, please email us with the draw date,
          game name, and the numbers you believe are correct. We will verify against the official source and correct any errors promptly.
        </p>
      </div>

      <div className="disclaimer">
        <strong>Note:</strong> Lotto Number Generator is a free entertainment tool. We cannot assist with official lottery ticket
        purchases, prize claims, or disputes with lottery operators. For those matters, please contact your local lottery authority
        directly.
      </div>
    </article>
  );
}

export function ContactBodyKo({ lp }: { lp: Lp }) {
  return (
    <article className="about-section">
      <h1>연락처</h1>
      <p>
        <strong>로또 번호 생성기</strong>에 관심을 가져주셔서 감사합니다. 도구 사용법에 대한 질문, 버그 신고, 비즈니스 문의 등
        어떤 사항이든 아래 안내에 따라 연락해 주세요. 모든 문의에 성실히 답변드리며, 이용자 여러분의 피드백을 소중히 여기고 있습니다.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>일반 문의</h2>
      <p>
        난수 생성 원리, 역대 당첨 번호 매칭 기능, 파워볼·메가밀리언·로또 6/45 번호 범위 등 일반적인 질문은 아래 이메일로 보내주세요.
        문의 전 <Link to={lp('/faq')}>FAQ 페이지</Link>를 먼저 확인하시면 빠르게 답을 찾으실 수 있습니다.
        <Link to={lp('/about')}>소개</Link> 페이지와 <Link to={lp('/story')}>이야기</Link> 페이지에서도 유용한 정보를 확인할 수 있습니다.
      </p>
      <p style={{ textAlign: 'center', fontSize: '1.1em', margin: '16px 0' }}>
        <strong>
          <a href="mailto:contact@lotto-number-generator.com">contact@lotto-number-generator.com</a>
        </strong>
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>버그 신고 및 피드백</h2>
      <p>
        생성기 사용 중 기술적인 문제가 발생하면 알려주세요. 번호가 표시되지 않거나, 복사·저장 기능이 작동하지 않거나, 화면 표시 문제 등
        어떤 오류든 보고해 주시면 빠르게 조사하여 해결하겠습니다. 버그 신고 시 다음 정보를 포함해 주시면 큰 도움이 됩니다:
      </p>
      <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.8, color: 'var(--text-secondary)' }}>
        <li>사용 중인 기기와 브라우저 (예: 아이폰 사파리, 윈도우 크롬, 갤럭시 삼성 인터넷 등)</li>
        <li>문제 발생 시 선택한 복권 게임 (파워볼, 메가밀리언, 로또 6/45)</li>
        <li>발생한 현상과 기대한 동작에 대한 간단한 설명</li>
        <li>가능하다면 스크린샷 — 문제 재현에 큰 도움이 됩니다</li>
      </ul>
      <p>
        새로운 기능 제안이나 UI 개선 의견, 추가 지원을 원하는 복권 게임 등의 아이디어도 환영합니다.
        여러분의 피드백이 개발 방향에 직접적인 영향을 줍니다.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>응답 시간</h2>
      <p>
        소규모 팀으로 운영되고 있어 일반 문의 및 버그 신고에 대한 답변은 <strong>영업일 기준 3~5일</strong> 정도 소요될 수 있습니다.
        핵심 기능(번호 생성, 데이터 정확성)에 영향을 미치는 문제를 우선 처리합니다. 기능 요청 및 일반 질문은 순차적으로 검토합니다.
        급한 사안의 경우 이메일 제목에 해당 내용을 명시해 주시면 가능한 빠르게 대응하겠습니다.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>비즈니스 및 광고 문의</h2>
      <p>
        파트너십, 광고 게재, 미디어 취재 등 비즈니스 관련 논의는 이메일 제목에 &ldquo;비즈니스 문의&rdquo;를 포함하여{' '}
        <a href="mailto:contact@lotto-number-generator.com">contact@lotto-number-generator.com</a>으로 보내주세요.
        전 세계 이용자에게 무료로 즐길 수 있는 교육적이고 재미있는 복권 도구를 제공하려는 저희의 미션에 부합하는 협업에 열려 있습니다.
      </p>

      <h2 style={{ fontSize: '1.15em', marginTop: 20, marginBottom: 8 }}>개인정보 및 데이터 요청</h2>
      <p>
        데이터 처리 방식에 대한 질문이나 개인정보 관련 권리 행사는 <Link to={lp('/privacy')}>개인정보처리방침</Link>을 참조해 주세요.
        이메일 제목에 &ldquo;개인정보 요청&rdquo;을 포함하여 직접 연락하셔도 됩니다. 관련 법규에 따라 신속하게 처리하겠습니다.
      </p>

      <div className="disclaimer">
        <strong>참고:</strong> 로또 번호 생성기는 무료 오락 도구입니다. 공식 복권 구매, 당첨금 수령, 복권 운영 기관 관련 분쟁은
        해당 관할 구역의 복권 당국에 직접 문의해 주세요.
      </div>
    </article>
  );
}
